// Rechnungsliste (Ein-/Ausgang) — Pagero-Muster: ein Arbeitsbereich, Richtung als
// Kontext, "Nur Handlungsbedarf" als Standard-Toggle im Eingang; Lexoffice-Muster:
// Formatbadge je Zeile; Zwei-Spuren-Chips (Business + Transport) getrennt.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Search, RefreshCw } from 'lucide-react';
import { Card, BusinessChip, TransportChip, FormatBadge, money, fmtDate, inputCls, btnPrimary, btnGhost } from './ui';
import PlantFilter from './PlantFilter';
import { listInvoices, uploadInvoice } from '../api';

// Mobile-Karte statt Tabellenzeile (Row->Card-Muster): breite Mehrspalten-Tabellen
// scrollen auf dem Smartphone sonst horizontal. Kopf = Nummer/Partner + Betrag,
// darunter Format-/Status-Chips, dann die restlichen Spalten als Label/Wert-Raster.
// Nutzt exakt dieselben Spaltenlabels wie die Desktop-Tabelle (keine neuen i18n-Keys).
const MobileInvoiceRow = ({ r, isDarkMode, t, onOpenInvoice }) => {
  const textMain = isDarkMode ? 'text-white/85' : 'text-slate-700';
  const textMuted = isDarkMode ? 'text-white/55' : 'text-slate-500';
  const meta = (label, value, mono) => (
    <div className="min-w-0">
      <div className={`text-[11px] uppercase tracking-wide ${textMuted}`}>{label}</div>
      <div className={`text-xs truncate ${mono ? 'font-mono' : ''} ${textMain}`}>{value}</div>
    </div>
  );
  return (
    <button
      onClick={() => onOpenInvoice(r.id)}
      className={`w-full text-left rounded-xl p-3.5 border transition-colors ${isDarkMode
        ? 'bg-white/5 border-white/10 hover:bg-white/[0.08]'
        : 'bg-white border-slate-200 hover:bg-slate-50'}`}
    >
      {/* Zeile 1: Rechnungsnummer bekommt die volle Breite -> wird nicht mehr vom Betrag abgeschnitten */}
      <div className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>
        {r.invoice_number || t('einvoicing.noNumber')}
      </div>
      {/* Zeile 2: Verkaeufer links (gedaempft), Betrag rechts hervorgehoben + rechtsbuendig
          (tabular-nums -> Betraege fluchten untereinander und sind gut vergleichbar) */}
      <div className="flex items-baseline justify-between gap-3 mt-0.5">
        <div className={`text-xs truncate min-w-0 ${textMuted}`}>{r.counterparty_name}</div>
        <div className={`flex-shrink-0 text-[15px] font-bold tabular-nums whitespace-nowrap ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          {money(r.total_payable, r.currency)}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
        <FormatBadge format={r.source_format} isDarkMode={isDarkMode} />
        <BusinessChip state={r.state} isDarkMode={isDarkMode} t={t} />
        <TransportChip state={r.transport_state} isDarkMode={isDarkMode} t={t} />
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">
        {meta(t('einvoicing.colPlant'), r.plant || '—', true)}
        {meta(t('einvoicing.colMatch'), r.match_status ? t(`einvoicing.match.${r.match_status}`) : '—')}
        {meta(t('einvoicing.colIssueDate'), fmtDate(r.issue_date))}
        {meta(t('einvoicing.colDueDate'), fmtDate(r.due_date))}
      </div>
    </button>
  );
};

const InvoiceList = ({ user, isDarkMode, t, direction, initialFilters = {}, plants = [], onOpenInvoice, showSuccess, showError }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [state, setState] = useState(initialFilters.state || '');
  const [plant, setPlant] = useState(''); // '' = Alle Werke
  const [needsAction, setNeedsAction] = useState(
    initialFilters.needsAction !== undefined ? initialFilters.needsAction : direction === 'inbound'
  );
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listInvoices(user, { direction, q, state, needsAction, plant }));
    } catch (e) {
      showError?.(t('einvoicing.listLoadError') + ': ' + e.message);
    } finally { setLoading(false); }
  }, [user, direction, q, state, needsAction, plant, showError, t]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (file) => {
    if (!file) return;
    try {
      const res = await uploadInvoice(user, file);
      if (res.ok === false) { showError?.(res.error); return; }
      const v = res.validation;
      if (v && !v.valid) {
        showError?.(t('einvoicing.uploadValidationFailed', { n: v.errors.length }));
      } else {
        showSuccess?.(t('einvoicing.uploadOk', { format: res.format?.toUpperCase?.() || res.format }));
      }
      await load();
      if (res.invoiceId) onOpenInvoice(res.invoiceId);
    } catch (e) {
      showError?.(t('einvoicing.uploadError') + ': ' + e.message);
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const STATES = direction === 'inbound'
    ? ['received', 'validated', 'validation_failed', 'in_approval', 'approved', 'rejected', 'posted_to_sap', 'archived']
    : ['draft', 'generated', 'sent', 'archived'];

  const th = `text-left text-[11px] uppercase tracking-wide font-semibold px-3 py-2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`;
  const td = `px-3 py-2.5 text-sm ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`;

  return (
    <Card isDarkMode={isDarkMode}>
      {/* Filterzeile + Upload */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative w-full sm:w-auto">
          <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('einvoicing.searchPlaceholder')}
            className={`${inputCls(isDarkMode)} pl-9 w-full sm:w-56`} />
        </div>
        <select value={state} onChange={(e) => setState(e.target.value)} className={inputCls(isDarkMode)}>
          <option value="">{t('einvoicing.allStates')}</option>
          {STATES.map((s) => <option key={s} value={s}>{t(`einvoicing.state.${s}`)}</option>)}
        </select>
        {plants.length > 0 && (
          <PlantFilter plants={plants} value={plant} onChange={setPlant} isDarkMode={isDarkMode} t={t} />
        )}
        {direction === 'inbound' && (
          <label className={`flex items-center gap-2 text-sm cursor-pointer ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
            <input type="checkbox" checked={needsAction} onChange={(e) => setNeedsAction(e.target.checked)} className="accent-violet-600" />
            {t('einvoicing.needsActionOnly')}
          </label>
        )}
        <div className="flex-1" />
        <button onClick={load} className={btnGhost(isDarkMode)} title={t('common.refresh')}><RefreshCw className="w-4 h-4" /></button>
        {direction === 'inbound' && (
          <>
            <input ref={fileRef} type="file" accept=".xml,.pdf" className="hidden"
              onChange={(e) => onUpload(e.target.files?.[0])} />
            <button onClick={() => fileRef.current?.click()} className={btnPrimary(isDarkMode)}>
              <span className="flex items-center gap-2"><Upload className="w-4 h-4" />{t('einvoicing.uploadButton')}</span>
            </button>
          </>
        )}
      </div>

      {/* Desktop: Tabelle (ab md). Auf schmalen Fenstern scrollt sie im eigenen Kasten. */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className={isDarkMode ? 'border-b border-white/10' : 'border-b border-slate-200'}>
              <th className={th}>{t('einvoicing.colNumber')}</th>
              <th className={th}>{t('einvoicing.colCounterparty')}</th>
              <th className={th}>{t('einvoicing.colPlant')}</th>
              <th className={th}>{t('einvoicing.colIssueDate')}</th>
              <th className={th}>{t('einvoicing.colDueDate')}</th>
              <th className={`${th} text-right`}>{t('einvoicing.colAmount')}</th>
              <th className={th}>{t('einvoicing.colFormat')}</th>
              <th className={th}>{t('einvoicing.colStatus')}</th>
              <th className={th}>{t('einvoicing.colMatch')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} onClick={() => onOpenInvoice(r.id)}
                className={`cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-white/5 border-b border-white/5' : 'hover:bg-slate-50 border-b border-slate-100'}`}>
                <td className={`${td} font-medium`}>{r.invoice_number || t('einvoicing.noNumber')}</td>
                <td className={td}>{r.counterparty_name}</td>
                <td className={`${td} font-mono text-xs`}>{r.plant || '—'}</td>
                <td className={td}>{fmtDate(r.issue_date)}</td>
                <td className={td}>{fmtDate(r.due_date)}</td>
                <td className={`${td} text-right tabular-nums`}>{money(r.total_payable, r.currency)}</td>
                <td className={td}><FormatBadge format={r.source_format} isDarkMode={isDarkMode} /></td>
                <td className={td}>
                  <span className="flex gap-1.5 flex-wrap">
                    <BusinessChip state={r.state} isDarkMode={isDarkMode} t={t} />
                    <TransportChip state={r.transport_state} isDarkMode={isDarkMode} t={t} />
                  </span>
                </td>
                <td className={td}>{r.match_status ? t(`einvoicing.match.${r.match_status}`) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobil (bis md): je Zeile eine kompakte Karte statt horizontal scrollender Tabelle */}
      <div className="md:hidden space-y-2.5">
        {rows.map((r) => (
          <MobileInvoiceRow key={r.id} r={r} isDarkMode={isDarkMode} t={t} onOpenInvoice={onOpenInvoice} />
        ))}
      </div>

      {rows.length === 0 && (
        <p className={`text-sm py-8 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          {loading ? t('einvoicing.loading') : t('einvoicing.listEmpty')}
        </p>
      )}
    </Card>
  );
};

export default InvoiceList;
